// apps/web/src/routes/layout.tsx
import { Outlet, NavLink, useNavigate } from 'react-router-dom'
import { Separator } from '@viecxanh/chat-ui/separator'
import { Avatar, AvatarFallback } from '@viecxanh/chat-ui/avatar'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@viecxanh/chat-ui/dropdown-menu'
import { Badge } from '@viecxanh/chat-ui/badge'
import { useAuth } from '../hooks/use-auth'
import { useSocket } from '../hooks/use-socket'
import { useChatStore } from '../stores/chat-store'
import { OnlineIndicator } from '../components/online-indicator'
import { cn } from '@viecxanh/chat-ui/utils'

const NAV = [
  { to: '/dashboard', label: 'Tổng quan', icon: '📊' },
  { to: '/conversations', label: 'Hội thoại', icon: '💬' },
  { to: '/channels', label: 'Kênh', icon: '📡' },
  { to: '/assignment-rules', label: 'Phân công', icon: '⚙️' },
  { to: '/quick-replies', label: 'Trả lời nhanh', icon: '⚡' },
  { to: '/analytics', label: 'Thống kê', icon: '📈' },
  { to: '/settings', label: 'Cài đặt', icon: '🔧' },
]

export function Layout() {
  const { staff, logout, isAuthenticated } = useAuth()
  useSocket(isAuthenticated) // Only connect socket after login
  const navigate = useNavigate()
  const { isConnected, unreadCounts } = useChatStore()
  const totalUnread = Object.values(unreadCounts).reduce((a, b) => a + b, 0)

  const handleLogout = async () => {
    await logout()
    void navigate('/login')
  }

  return (
    <div className="flex h-screen overflow-hidden bg-slate-50">
      {/* Sidebar */}
      <aside className="flex w-56 flex-col bg-sidebar text-sidebar-fg">
        {/* Logo */}
        <div className="flex items-center gap-2 px-4 py-4 border-b border-white/10">
          <div className="h-7 w-7 rounded-md bg-brand flex items-center justify-center text-white font-bold text-sm">
            XV
          </div>
          <span className="font-semibold text-sm">Chat Service</span>
          <OnlineIndicator isOnline={isConnected} className="ml-auto" />
        </div>

        {/* Nav */}
        <nav className="flex-1 overflow-y-auto py-2">
          {NAV.map(({ to, label, icon }) => (
            <NavLink
              key={to}
              to={to}
              className={({ isActive }) =>
                cn(
                  'flex items-center gap-3 px-4 py-2.5 text-sm transition-colors hover:bg-white/10',
                  isActive ? 'bg-white/15 font-medium' : 'text-sidebar-fg/80',
                )
              }
            >
              <span>{icon}</span>
              <span className="flex-1">{label}</span>
              {to === '/conversations' && totalUnread > 0 && (
                <Badge className="h-4 min-w-4 bg-red-500 text-white text-[10px] px-1">
                  {totalUnread > 99 ? '99+' : totalUnread}
                </Badge>
              )}
            </NavLink>
          ))}
        </nav>

        <Separator className="bg-white/10" />

        {/* User menu */}
        {staff && (
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <button className="flex items-center gap-2 px-4 py-3 hover:bg-white/10 transition-colors w-full text-left">
                <Avatar className="h-7 w-7">
                  <AvatarFallback className="bg-brand text-white text-xs">
                    {staff.name.slice(0, 2).toUpperCase()}
                  </AvatarFallback>
                </Avatar>
                <div className="flex-1 min-w-0">
                  <p className="text-xs font-medium truncate">{staff.name}</p>
                  <p className="text-[10px] text-sidebar-fg/60 truncate">{staff.email}</p>
                </div>
              </button>
            </DropdownMenuTrigger>
            <DropdownMenuContent side="top" className="w-44">
              <DropdownMenuItem onClick={() => void handleLogout()}>Đăng xuất</DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        )}
      </aside>

      {/* Main */}
      <main className="flex-1 overflow-y-auto">
        <Outlet />
      </main>
    </div>
  )
}
